/**
 * `triggerLogout` is required INSIDE the function, not imported at module scope, and that is the
 * only reason this module is safe.
 *
 * The store's own body calls `migrateOldStateToPersist()` at module-EVAL time. `commonHelpers`
 * imports the store, so a static import here would put this file in a cycle WITH the store — and
 * then that eval-time call could run before this module's own imports were assigned. Audit check H
 * flagged exactly that. A `require` in a function body is not a static edge, so no cycle forms.
 */
function triggerLogout(): void {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  require("../../helpers/commonHelpers").triggerLogout();
}
import { getLocalStorage, setLocalStorage } from "../localStorage";
import { isEmpty } from "lodash";
import { REDUX_PERSIST_STATE } from "../constants";
import { SENTRY_EVENTS } from "../sentryHelpers";
import { logEvent } from "../../effects-bridge";
import { OLD_STATE_KEY } from "./shared";

export * from "./shared";

/**
 * WEB half of `utils/migrateState` — the one-time migration itself.
 *
 * One-time migration from the old manual localStorage persistence ("state" key)
 * to the redux-persist format ("persist:root" key).
 *
 * Old system stored a flat JSON object under localStorage["state"]:
 *   { globalStore: {...}, catalogStore: {...}, ... }
 *
 * redux-persist stores each slice as a nested JSON string under localStorage["persist:root"]:
 *   { globalStore: '{"..."}', catalogStore: '{"..."}', _persist: '{"version":1,"rehydrated":true}' }
 *
 * This migration runs once: only when the old key exists and the new key does not.
 * On success the old key is removed. On any error it silently aborts so the app
 * still starts (user will lose persisted state but won't crash).
 *
 * ⚠️ `localStorage` is read DIRECTLY at call time rather than through a bridge — hazard 18(b).
 * This file is `.desktop`, so it only ever evaluates under webpack in a real browser, and the
 * app's own version did the same. The two `getLocalStorage`/`setLocalStorage` calls keep going
 * through core's wrapper because the app's did.
 *
 * 🔴 **THE ERROR REPORT IN THE CATCH HAS NEVER FIRED, AND THAT IS WHY IT IS A BRIDGE CALL HERE.**
 * The app version called `sendCaptureEvent(captureObj, SENTRY_EVENTS.primaryMigrationError)`.
 * That call could not succeed: this function runs during the STORE MODULE'S OWN EVALUATION
 * (the store calls it before it constructs the store), and `sendCaptureEvent` reads the store
 * singleton as its first statement — which is still in its temporal dead zone at that moment,
 * because `utils/sentryHelpers` imports the store and the store imports this file. Since
 * `sendCaptureEvent` is `async` and is called without `await`, the resulting TDZ `ReferenceError`
 * became an UNHANDLED REJECTION rather than a thrown error, so the app booted and nobody noticed.
 * Verified with a webpack 5 fixture reproducing the cycle, plus a control arm; all four cycle
 * members confirmed to share the `main` chunk of the production build.
 *
 * So this goes through the effects bridge's `logEvent`, which is UNWIRED on this platform and
 * therefore a no-op — i.e. the intended event does not fire, exactly as before. The day the app
 * wires `logEvent`, the report starts working for the first time. The app-side defect is filed
 * separately; it exists on the release branch independently of this move.
 */

const PERSIST_VERSION = 1;

/**
 * Keys that were actually saved in the old system AND exist in the new
 * redux-persist whitelist under the same name.
 *
 * Excluded keys and reasons:
 *
 * - "LoyaltyStore": new whitelist has "LoyaltyStore", but the old subscribe
 *   callback read from store.getState().ristaLoyaltyReducer — a key that
 *   never existed in the reducer map (the slice was registered as LoyaltyStore).
 *   The persisted value was always undefined, so there is nothing to migrate.
 *
 * - "myOrderStore": was collected in the old subscribe but was commented out
 *   inside saveState({}) so it was never actually written to localStorage.
 */
const MIGRATEABLE_KEYS = [
  "globalStore",
  "catalogStore",
  "catalogMapStore",
  "posSettingsStore",
  "customerStore",
  "registerStore",
  "loginStore",
  "ItemsStatusStore",
  "OutletStatusStore",
  "printerStore",
  "cashRegisterStore",
  "businessStore",
  "notificationsStore",
  "LicenceExpirySlice",
  "recommenderStore",
] as const;

export const MIGRATION_COMPLETED = "migrationCompleted";
export const FALLBACK_MIGRATION_COMPLETED = "fallbackMigrationCompleted";
export const LOST_DATA_DEVICE = "lostDataDevice";

export function migrateOldStateToPersist(): void {
  try {
    const oldStateStr = localStorage.getItem(OLD_STATE_KEY);
    const newStateStr = localStorage.getItem(REDUX_PERSIST_STATE);

    // Only migrate when old state exists and new persist state does not yet
    if (!oldStateStr) {
      if (!getLocalStorage(MIGRATION_COMPLETED)) {
        setLocalStorage(LOST_DATA_DEVICE, "true");
      }
      return;
    }
    console.log("Migrating old persisted state to new format...");
    const oldState = JSON.parse(oldStateStr);
    const persistRoot: Record<string, string> = {};

    for (const key of MIGRATEABLE_KEYS) {
      if (oldState[key] !== undefined && oldState[key] !== null) {
        if (key === "printerStore" && oldState[key]) {
          let defaultPrinter;
          if (!isEmpty(oldState[key].defaultPrinterDetails)) {
            defaultPrinter = oldState[key].defaultPrinterDetails;
          } else if (!isEmpty(oldState[key].defaultPrinter)) {
            defaultPrinter = oldState[key].defaultPrinter;
          } else {
            defaultPrinter = {};
          }
          oldState[key].defaultPrinter = structuredClone(defaultPrinter);
          delete oldState[key].defaultPrinterDetails;
        }
        persistRoot[key] = JSON.stringify(oldState[key]);
      }
    }

    persistRoot._persist = JSON.stringify({ version: PERSIST_VERSION, rehydrated: true });
    localStorage.removeItem(OLD_STATE_KEY);
    localStorage.setItem(REDUX_PERSIST_STATE, JSON.stringify(persistRoot));
    setLocalStorage(MIGRATION_COMPLETED, "true");

    setTimeout(() => {
      triggerLogout(); // Force logout to clear any in-memory state that might be inconsistent with the migrated persisted state. This is a safety measure to prevent potential issues from stale data.
    }, 4000);
  } catch (error) {
    const captureObj = {

    }
    logEvent(SENTRY_EVENTS.primaryMigrationError, captureObj);
    console.error("Migration failed:", error);
    // Migration failed — app will start with empty persisted state.
    // Do not rethrow; a failed migration must never crash the app.
  }
}
